import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TOKENS } from '../tokens';
import { ChapterTag } from './Details';

const USE_CASES = [
  {
    num: '01', icon: '📦',
    title: 'Warehouse & Storage',
    body: 'Secure, road-accessible, metro-connected. Ideal for last-mile logistics, goods storage, or cold chain operations. Central Delhi premium — without Central Delhi headaches.',
  },
  {
    num: '02', icon: '🏢',
    title: 'Office Conversion',
    body: 'Convert this ground floor unit into a premium commercial office. Independent entrance, Central Delhi address on your letterhead, metro at the door. The kind of workspace that impresses clients before they walk in.',
  },
  {
    num: '03', icon: '⚙️',
    title: 'Light Manufacturing',
    body: "Industrial zoning unlocks garments, packaging, printing, and assembly operations. A Central Delhi address with a separate entrance and 225 sq. yards of space is a competitive moat few businesses can match.",
  },
  {
    num: '04', icon: '📈',
    title: 'Investment & Redevelopment',
    body: "Already occupied, already earning. Buy it today and collect rent from Day 1. Commercial floor in Central Delhi appreciates at a rate very few asset classes can match. This isn't a property — it's a position.",
  },
];

function OppCard({ card, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-hover
      style={{
        background: hovered ? '#0A1228' : '#0D1535',
        padding: 'clamp(40px,5vw,64px) clamp(28px,4vw,52px)',
        position: 'relative', overflow: 'hidden',
        transition: 'background 0.4s',
        cursor: 'none',
      }}
    >
      {/* Ghost number */}
      <div style={{
        position: 'absolute', top: 22, right: 32,
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: '5rem', fontWeight: 300, lineHeight: 1,
        color: hovered ? 'rgba(200,169,106,0.1)' : 'rgba(200,169,106,0.055)',
        transform: hovered ? 'scale(1.06)' : 'scale(1)',
        transition: 'color 0.4s, transform 0.4s',
        pointerEvents: 'none', userSelect: 'none',
      }}>
        {card.num}
      </div>

      {/* Icon */}
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: hovered ? 'rgba(200,169,106,0.14)' : 'rgba(200,169,106,0.08)',
        border: '1px solid rgba(200,169,106,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.4rem', marginBottom: 26,
        boxShadow: hovered ? '0 0 24px rgba(200,169,106,0.12)' : 'none',
        transition: 'background 0.3s, box-shadow 0.3s',
      }}>
        {card.icon}
      </div>

      <h3 style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: '1.7rem', fontWeight: 400,
        color: '#fff', lineHeight: 1.2, marginBottom: 14,
      }}>
        {card.title}
      </h3>
      <p style={{ fontSize: '0.84rem', fontWeight: 300, lineHeight: 1.85, color: TOKENS.MUTED }}>
        {card.body}
      </p>
    </motion.div>
  );
}

export default function Opportunity() {
  return (
    <section id="the-opportunity" style={{ background: '#050A1A', padding: 'clamp(80px,10vw,140px) clamp(20px,4vw,48px)' }}>
      <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 90px' }}>
        <ChapterTag text="Chapter 04 · What You Can Build" />
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(2.4rem, 5vw, 4.5rem)',
            fontWeight: 300, lineHeight: 1.1, color: '#fff',
          }}
        >
          Four paths.<br /><em style={{ fontStyle: 'italic', color: TOKENS.GOLD }}>One address.</em>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          style={{ fontSize: '1rem', fontWeight: 300, lineHeight: 1.8, color: TOKENS.MUTED, marginTop: 18 }}
        >
          Whether you're an investor, an operator, or a developer — this property has a story for you.
        </motion.p>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2, maxWidth: 1100, margin: '0 auto',
        background: '#0A1030', borderRadius: 20, overflow: 'hidden',
      }} className="opp-grid">
        {USE_CASES.map((card, i) => (
          <OppCard key={card.num} card={card} delay={i * 0.08} />
        ))}
      </div>

      <style>{`
        @media (max-width: 640px) {
          .opp-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
